import { AppDispatch, RootState } from "app/store";
import { initialAsync, downloadAsync } from "features/download";
import {
  selectDownloadList,
  selectInitializeState,
  selectPopupTarget,
  selectExcuteState,
} from "features/download/selectors";
import {
  DownloadProduct,
  InitializeState,
  ExcuteState,
} from "features/download/types";
import React, { useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import OmronHeader from "components/OmronHeader";
import OmronFooter from "components/OmronFooter";
import OmronSidebar from "components/OmronSidebar";
import { logoutAsync, userAsync } from "features/auth/operations";
import { selectUserState } from "features/auth/selectors";
import PageTitle from "components/PageTitle";
import { useTranslation } from "react-i18next";
import {
  clearPopupTarget,
  changePopupTarget,
} from "features/download/downloadSlice";
import {
  Loading,
  Message,
  Table,
  TableHeader,
  ToolTip,
  getDate,
} from "components/Table";
import DownloadPopup from "./DownloadPopup";
import {
  ExpansionTable,
  ExpansionTableGroupContent,
  Column,
  Item,
} from "components/OmronExpansionTable";

const DownloadPage: React.FC = (): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const products = useSelector<RootState, DownloadProduct[]>((state) =>
    selectDownloadList(state)
  );
  const sortedProducts = useMemo(() => {
    let sorted = [...products]
    sorted.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    sorted.sort((a, b) => a.groupOrder - b.groupOrder)
    return sorted
  }, [products])
  const sortedGroupProducts = useMemo(() => {
    const sorted = [...sortedProducts]
    const sortedGroup: Record<string, { libVerDisplay: boolean, items: DownloadProduct[] }> = {}
    for (const product of sorted) {
      const order = String(product.groupOrder)
      if (!sortedGroup[order]) sortedGroup[order] = { libVerDisplay: false, items: [] }
      sortedGroup[order].items.push(product)
      if (!!product.libVer && !sortedGroup[order].libVerDisplay) sortedGroup[order].libVerDisplay = true
    }
    return sortedGroup
  }, [sortedProducts])
  const initializeState = useSelector<RootState, InitializeState>((state) =>
    selectInitializeState(state)
  );

  const user = useSelector(selectUserState);
  const popupTarget = useSelector(selectPopupTarget);
  const excuteState = useSelector(selectExcuteState);

  useEffect(() => {
    dispatch(userAsync());
    dispatch(initialAsync());
  }, [dispatch]);

  const logout = useCallback(async () => {
    const { meta } = await dispatch(logoutAsync());
    if (meta.requestStatus === "rejected") {
      return false;
    }
    window.location.pathname = "/";
    return true;
  }, [dispatch]);

  const onDownload = useCallback(
    (id: string, name: string, version: string, libVer: string, releaseDate: string, downloadId: string) => {
      // ダウンロードポップアップ表示
      dispatch(changePopupTarget({id, name, version, libVer, releaseDate }));
      dispatch(downloadAsync({ downloadId }));
    },
    [dispatch]
  );

  return (
    <div>
      {popupTarget && (
        <DownloadPopup
          downloadUrl={popupTarget.downloadurl}
          name={popupTarget.name}
          productId={popupTarget.id}
          version={popupTarget.version}
          libVer={popupTarget.libVer}
          releaseDate={popupTarget.releaseDate}
          filesize={popupTarget.filesize}
          onCancel={() => {
            dispatch(clearPopupTarget());
          }}
        />
      )}
      <Wrap>
        <OmronHeader
          link={{ label: t("common.label.breadcrumbsHome"), path: "/" }}
          label={[t("common.label.breadcrumbsDownload")]}
          user={user}
        />
        <OmronSidebar download onLogout={logout} />
        <Main executestate={excuteState}>
          <PageTitle title={t("downloadPage.label.title")} text="" />
          {initializeState === "Initializing" && (
            <div>
              <Loading />
            </div>
          )}
          {initializeState === "initializeError" && (
            <div>
              <Message style={{ color: "red" }}>
                {t("downloadPage.message.faildGetData")}
              </Message>
            </div>
          )}
          {initializeState === "Initialized" && products.length === 0 && (
            <div>
              <Message>{t("downloadPage.message.dataEmpty")}</Message>
            </div>
          )}
          {
            initializeState === 'Initialized' &&
            Object.values(sortedGroupProducts).map(({ libVerDisplay, items }) => {
              return libVerDisplay
                ? <ExpansionTable
                  groupBy="groupName"
                  fallbackGroupName={t("downloadPage.table.others")}
                  toFallbackGroup={["Default"]}
                  items={items}
                  headers={[
                    t("downloadPage.table.productName"),
                    `${t("downloadPage.table.version")} / ${t("downloadPage.table.libVer")}`,
                    t("downloadPage.table.releaseDate"),
                    t("downloadPage.table.download")
                  ]}
                  tooltips={[
                    t("downloadPage.help.productName"),
                    t("downloadPage.help.version"),
                    t("downloadPage.help.releaseDate"),
                    t("downloadPage.help.downloadDetail")
                  ]}
                >
                  <ExpansionTableGroupContent>
                    {{
                      content: (downloadProduct: DownloadProduct) =>
                        <>
                          <Item>{downloadProduct["name"]}</Item>
                          <Item width="14%">
                            {`${!!downloadProduct["version"]
                              ? downloadProduct["version"]
                              : "-"} 
                            / ${!!downloadProduct["libVer"]
                                ? downloadProduct["libVer"]
                                : "-"
                              }`}
                          </Item>
                          <Item width="14%">{downloadProduct["releaseDate"]}</Item>
                          <ButtonColumn>
                            <Button
                              executestate={excuteState}
                              disabled={downloadProduct.downloadId === ""}
                              onClick={() => {
                                onDownload(downloadProduct.id, downloadProduct.name, downloadProduct.version, downloadProduct.libVer, downloadProduct.releaseDate, downloadProduct.downloadId);
                              }}
                            >
                              {t("downloadPage.button.download")}
                            </Button>
                          </ButtonColumn>
                        </>
                    }}
                  </ExpansionTableGroupContent>
                </ExpansionTable>
                : <ExpansionTable
                  groupBy="groupName"
                  fallbackGroupName={t("downloadPage.table.others")}
                  toFallbackGroup={["Default"]}
                  items={items}
                  headers={[
                    t("downloadPage.table.productName"),
                    t("downloadPage.table.version"),
                    t("downloadPage.table.releaseDate"),
                    t("downloadPage.table.download")
                  ]}
                  tooltips={[
                    t("downloadPage.help.productName"),
                    t("downloadPage.help.version"),
                    t("downloadPage.help.releaseDate"),
                    t("downloadPage.help.downloadDetail")
                  ]}
                >
                  <ExpansionTableGroupContent>
                    {{
                      content: (downloadProduct: DownloadProduct) =>
                        <>
                          <Item>{downloadProduct["name"]}</Item>
                          <Item width='14%'>{downloadProduct['version']}</Item>
                          <Item width='14%'>{downloadProduct['releaseDate']}</Item>
                          <ButtonColumn>
                            <Button
                              executestate={excuteState}
                              disabled={downloadProduct.downloadId === ""}
                              onClick={() => {
                                onDownload(downloadProduct.id, downloadProduct.name, downloadProduct.version, downloadProduct.libVer, downloadProduct.releaseDate, downloadProduct.downloadId);
                              }}
                            >
                              {t("downloadPage.button.download")}
                            </Button>
                          </ButtonColumn>
                        </>
                    }}
                  </ExpansionTableGroupContent>
                </ExpansionTable>
            })
          }
        </Main>
        <OmronFooter />
      </Wrap>
    </div>
  );
};

export default DownloadPage;

const Wrap = styled.div`
  display: grid;
  grid-template-rows: 76px 40px 1fr auto;
  grid-template-columns: 20rem 1fr;
  grid-template-areas: "header header" "breadc breadc" "side   main" "side   footer";
  min-height: 100vh;
`;
const Main = styled.main<{
  executestate: ExcuteState | undefined;
}>`
  grid-area: main;
  padding: 3.2rem 50px;
  ${({ executestate }) =>
    executestate === "Excuting" &&
    css`
      cursor: wait;
    `}
`;

const ButtonColumn = styled(Column)`
  width: 14%;
`;

const Button = styled.a<{
  disabled?: boolean;
  executestate: ExcuteState | undefined;
}>`
  display: block;
  padding: 10px 12px;
  color: #ffffff;
  text-decoration: none;
  background: #005eb8;
  border: 1px #005eb8 solid;
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  cursor: pointer;
  :hover {
    background: rgba(0, 94, 184, 0.7);
  }
  ${({ disabled }) =>
    disabled &&
    css`
      color: #999999;
      background: #f5f5f5;
      border: 1px #e6e6e6 solid;
      pointer-events: none;
      :hover {
        background: #fafafa;
      }
    `}
  ${({ executestate }) =>
    executestate === "Excuting" &&
    css`
      pointer-events: none;
    `}
`;
